/**
 * Stage 1 — Cuts.
 *
 * One clip per screen, standalone: its own waveform, its own loop, nothing of
 * the neighbours drawn. The human answers ONE question: does this clip hold
 * exactly what it says it holds?
 *
 *   ✓ Good            — yes; approved, next clip
 *   "<x> + <this>"    — no; this clip should also contain the neighbouring
 *                       sound named on the button
 *
 * There are no split/join/drag controls. The option buttons are built from
 * what the neighbours actually contain (we always know their words), so the
 * user only ever picks a description of what they hear. Whether fulfilling it
 * means moving a boundary, moving a word, or fusing two records is decided
 * internally — never asked.
 */

import { useCallback, useEffect, useState } from 'react';
import type { PageProps } from './types';
import { approveCut, getClip, getPendingCutSegments, getSegmentsByTask, getTaskAudio, listTasks } from '../db';
import { decodeAudioBlob } from '../services/audioSlicer';
import {
  joinClip,
  setClipBounds,
  takeWordAcross,
  transferEdgeWord,
  type CutOpResult,
} from '../services/cutOps';
import { wordsOf } from '../services/recut';
import type { Segment } from '../types';
import {
  Button,
  ClipEditor,
  ClipWave,
  EmptyState,
  KindBadge,
  clipPlayer,
  pauseAllAudio,
  playClipBlob,
  releaseClip,
} from '../components';

/** Short name for a clip's content, used in the option labels. */
function shortLabel(seg: Segment): string {
  const words = wordsOf(seg);
  if (words.length === 0) return seg.kind;
  if (words.length <= 2) return words.map((w) => w.text).join(' ');
  return `${words[0].text}…${words[words.length - 1].text}`;
}

interface Option {
  key: string;
  label: string;
  run: () => Promise<CutOpResult>;
}

export default function CutsPage({ focusTaskId }: PageProps) {
  const [queue, setQueue] = useState<Segment[]>([]);
  const [siblings, setSiblings] = useState<Segment[]>([]);
  const [buffers, setBuffers] = useState<Map<string, AudioBuffer>>(new Map());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [done, setDone] = useState(0);
  const [editing, setEditing] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const current = queue[0];
  const buffer = current ? buffers.get(current.taskId) : undefined;

  /* ---------------- queue ---------------- */

  useEffect(() => {
    let alive = true;
    void Promise.all([getPendingCutSegments(), listTasks()]).then(([pending, ts]) => {
      if (!alive) return;
      const rank = new Map(ts.map((t, i) => [t.id, i] as const));
      setQueue(
        [...pending].sort((a, b) => {
          if (a.taskId !== b.taskId) {
            if (focusTaskId) {
              if (a.taskId === focusTaskId) return -1;
              if (b.taskId === focusTaskId) return 1;
            }
            return (rank.get(a.taskId) ?? 0) - (rank.get(b.taskId) ?? 0);
          }
          return a.startSec - b.startSec;
        }),
      );
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [focusTaskId]);

  /* the recording's active records — for the neighbour options */
  const reloadSiblings = useCallback((taskId: string) => {
    void getSegmentsByTask(taskId).then((rows) =>
      setSiblings(rows.filter((s) => !s.splitAt && !s.obsoleteAt).sort((a, b) => a.startSec - b.startSec)),
    );
  }, []);
  useEffect(() => {
    if (current?.taskId) reloadSiblings(current.taskId);
  }, [current?.taskId, current?.id, reloadSiblings]);

  /* decode the recording once (records are stamps into this one file) */
  useEffect(() => {
    const tid = current?.taskId;
    if (!tid || buffers.has(tid)) return;
    let alive = true;
    void getTaskAudio(tid)
      .then((blob) => (blob ? decodeAudioBlob(blob) : null))
      .then((buf) => {
        if (alive && buf) setBuffers((m) => new Map(m).set(tid, buf));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [current?.taskId, buffers]);

  /* one object URL per recording — the modal loops the ORIGINAL file */
  useEffect(() => {
    const tid = current?.taskId;
    if (!tid) return;
    let url: string | null = null;
    let alive = true;
    void getTaskAudio(tid).then((blob) => {
      if (!alive || !blob) return;
      url = URL.createObjectURL(blob);
      setAudioUrl(url);
    });
    return () => {
      alive = false;
      setAudioUrl(null);
      if (url) URL.revokeObjectURL(url);
    };
  }, [current?.taskId]);

  /* ---------------- the clip's loop ---------------- */

  /* The clip is loaded into the ONE shared player, and only plays when the
     user asks. Nothing auto-plays: stacked autoplaying loops were heard as
     "all the clips playing together". */
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    setPlaying(false);
    pauseAllAudio();
    return () => releaseClip();
  }, [current?.id]);

  const togglePlay = useCallback(() => {
    const id = current?.id;
    if (!id) return;
    const el = clipPlayer();
    if (playing && !el.paused) {
      el.pause();
      setPlaying(false);
      return;
    }
    void getClip(id).then((blob) => {
      if (!blob) return;
      playClipBlob(blob);
      setPlaying(true);
    });
  }, [current?.id, playing]);

  /* ---------------- actions ---------------- */

  const run = useCallback(
    (op: () => Promise<CutOpResult>) => {
      if (busy) return;
      setBusy(true);
      pauseAllAudio();
      setPlaying(false);
      op()
        .then((out) => {
          setNote(null);
          if (out.created.length === 0) return;
          setQueue((q) => {
            const at = Math.max(0, q.findIndex((s) => out.obsoletedIds.includes(s.id)));
            const kept = q.filter((s) => !out.obsoletedIds.includes(s.id));
            kept.splice(at, 0, ...out.created.filter((c) => !kept.some((s) => s.id === c.id)));
            return kept;
          });
          if (current) reloadSiblings(current.taskId);
        })
        .catch((err: unknown) => setNote(err instanceof Error ? err.message : String(err)))
        .finally(() => setBusy(false));
    },
    [busy, current, reloadSiblings],
  );

  const good = useCallback(() => {
    if (!current || busy) return;
    setBusy(true);
    void approveCut(current.id)
      .then(() => {
        setNote(null);
        setDone((d) => d + 1);
        setQueue((q) => q.slice(1));
      })
      .catch((err: unknown) => setNote(err instanceof Error ? err.message : String(err)))
      .finally(() => setBusy(false));
  }, [current, busy]);

  /* ---------------- render ---------------- */

  if (loading) return null;

  if (!current) {
    return (
      <>
        <header className="page__head">
          <h1 className="page__title">Cuts</h1>
        </header>
        <EmptyState icon="✂️" title="No cuts waiting" hint="New uploads land here first." />
      </>
    );
  }

  /* Options: what this clip could also contain, from the real neighbours. */
  const at = siblings.findIndex((s) => s.id === current.id);
  const prev = at > 0 ? siblings[at - 1] : undefined;
  const next = at >= 0 && at < siblings.length - 1 ? siblings[at + 1] : undefined;
  const me = shortLabel(current);
  const options: Option[] = [];

  /* A word that SOUNDS here but is written beyond a silence/noise clip. The
     neighbour-only options can never reach it, so look past placeholders. */
  const acrossOn = (dir: 'prev' | 'next'): { word: string } | null => {
    const step = dir === 'prev' ? -1 : 1;
    const immediate = dir === 'prev' ? prev : next;
    if (!immediate || wordsOf(immediate).length > 0) return null; // only step over placeholders
    let j = at + step;
    while (j >= 0 && j < siblings.length && wordsOf(siblings[j]).length === 0) j += step;
    const donor = siblings[j];
    if (!donor || donor.verify !== 'pending') return null;
    const dw = wordsOf(donor);
    if (dw.length === 0) return null;
    return { word: dir === 'prev' ? dw[dw.length - 1].text : dw[0].text };
  };
  const acrossPrev = acrossOn('prev');
  const acrossNext = acrossOn('next');
  if (acrossPrev) {
    options.push({
      key: 'across-prev',
      label: `${acrossPrev.word} + ${me}`,
      run: () => takeWordAcross(current.id, 'prev'),
    });
  }
  if (acrossNext) {
    options.push({
      key: 'across-next',
      label: `${me} + ${acrossNext.word}`,
      run: () => takeWordAcross(current.id, 'next'),
    });
  }

  if (prev && prev.verify === 'pending') {
    const pw = wordsOf(prev);
    if (pw.length >= 2) {
      options.push({
        key: 'take-prev',
        label: `${pw[pw.length - 1].text} + ${me}`,
        run: () => transferEdgeWord(current.id, 'take-prev').then((o) => ({
          created: [o.updated, o.counterpart].sort((a, b) => a.startSec - b.startSec),
          obsoletedIds: o.obsoletedIds,
          status: o.status,
        })),
      });
    } else {
      options.push({
        key: 'join-prev',
        label: `${shortLabel(prev)} + ${me}`,
        run: () => joinClip(current.id, 'prev'),
      });
    }
  }
  if (next && next.verify === 'pending') {
    const nw = wordsOf(next);
    if (nw.length >= 2) {
      options.push({
        key: 'take-next',
        label: `${me} + ${nw[0].text}`,
        run: () => transferEdgeWord(current.id, 'take-next').then((o) => ({
          created: [o.updated, o.counterpart].sort((a, b) => a.startSec - b.startSec),
          obsoletedIds: o.obsoletedIds,
          status: o.status,
        })),
      });
    } else {
      options.push({
        key: 'join-next',
        label: `${me} + ${shortLabel(next)}`,
        run: () => joinClip(current.id, 'next'),
      });
    }
  }

  const len = current.endSec - current.startSec;

  return (
    <>
      <header className="page__head">
        <h1 className="page__title">Cuts</h1>
        <span className="page__sub">
          {done} done · {queue.length} left
        </span>
      </header>

      <section className="card col c2-card">
        <div className="row">
          <KindBadge kind={current.kind} />
          <span className="spacer" />
          <span className="mono c2-id">{current.name ?? `#${current.index + 1}`}</span>
        </div>
        <p className="c2-word" dir="auto">
          {current.heard}
        </p>
        <button type="button" className="c2-meta mono c2-metabtn" onClick={togglePlay}>
          {len.toFixed(2)}s ({Math.round(len * 1000)}ms) · {playing ? '⏸ tap to stop' : '▶ tap to play'}
        </button>
        {current.trace && current.trace.length > 0 && (
          <p className="mono c2-trace">made by: {current.trace.join(' → ')}</p>
        )}
      </section>

      {buffer ? (
        <ClipWave
          buffer={buffer}
          startSec={current.startSec}
          endSec={current.endSec}
          onTap={() => {
            pauseAllAudio();
            setPlaying(false);
            setEditing(true);
          }}
        />
      ) : (
        <div className="c2-loading mono">loading audio…</div>
      )}

      {note ? <p className="vp-note">{note}</p> : null}

      <Button block variant="primary" size="lg" onClick={good} disabled={busy}>
        ✓ Good
      </Button>

      {editing && buffer && audioUrl && (
        <ClipEditor
          buffer={buffer}
          audioUrl={audioUrl}
          startSec={current.startSec}
          endSec={current.endSec}
          reachSec={{
            before: prev ? current.startSec - prev.startSec - 0.1 : 1.5,
            after: next ? next.endSec - current.endSec - 0.1 : 1.5,
          }}
          title={current.name ?? 'Align the clip'}
          saving={busy}
          onCancel={() => setEditing(false)}
          onSave={(a, b) => {
            setEditing(false);
            run(() => setClipBounds(current.id, a, b));
          }}
        />
      )}

      {options.length > 0 && (
        <div className="col c2-opts">
          <p className="c2-opts__hint">or this clip is really…</p>
          {options.map((o) => (
            <Button key={o.key} block onClick={() => run(o.run)} disabled={busy} dir="auto">
              {o.label}
            </Button>
          ))}
        </div>
      )}
    </>
  );
}
